import { useConsentementAuBlocTiers } from '../features/contact/contact';
import {
  ADRESSE_DU_FORMULAIRE,
  ADRESSE_EMAIL,
  lAdresseEstOuverte,
  leFormulaireEstOuvert,
} from '../shared/contact-du-groupe';
import { leCadreVientDUneAutreOrigine } from '../shared/origines';
import { Bouton } from '../shared/ui/Bouton';
import { Paragraphe, Section } from '../shared/ui/Section';
import { Titre } from '../shared/ui/Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **PAGE 5 DES HUIT — LA SEULE PAGE DU SITE QUI PORTE UNE RESSOURCE TIERCE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-69 dans sa table : « page du **formulaire de contact** — **seule page qui porte le bloc
 * de code tiers** ». F-72 : « il vit sur une page à lui, et **jamais sur la page qui porte
 * l'écran de connexion**. Le bloc tiers **ne se charge que sur cette page, et sur aucune
 * autre du site**. » Et F-73 : « **une seule ressource tierce sur toute la surface**, le bloc
 * du formulaire de contact, sur sa page. »
 *
 * **POURQUOI JAMAIS SUR LA PAGE DE CONNEXION, ET LE MOTIF VAUT D'ÊTRE LU** : cette page-là
 * reçoit un mot de passe. Un tiers chargé dans la même page, quelle que soit sa forme,
 * partagerait le document où ce mot de passe se tape. F-71 le redit de son côté : « elle ne
 * partage aucune page avec le bloc de code tiers ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX CHEMINS, ET LE SECOND NE DÉPEND DE PERSONNE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-72 : « deux chemins, et **aucun des deux ne fait entrer quoi que ce soit dans le
 * produit** ». Le formulaire est un bloc d'une autre plateforme ; l'adresse email fait écrire
 * la personne « **depuis sa propre boîte** vers celle du groupe : **le produit n'est sur aucun
 * des deux bouts** ».
 *
 * **Le second est donc mis en avant quand le premier n'est pas là**, et le prérequis 21 est
 * ouvert : ce n'est pas une dégradation improvisée, c'est le seul chemin qui marche.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE FORMULAIRE NE DEMANDE RIEN SUR UN ÉLÈVE, ET CE N'EST PAS CE CODE QUI LE TIENT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-72 : « le formulaire ne demande **AUCUNE INFORMATION SUR UN ÉLÈVE**, ni nom, ni âge, ni
 * classe : il s'adresse à une structure ou à un tuteur, **jamais à une famille** ». **Les
 * champs vivent chez la plateforme qui génère le bloc**, donc cette règle se tient au moment
 * où le formulaire y est créé — prérequis 21 —, et **aucune ligne de ce dépôt ne peut la
 * vérifier**. Elle est écrite ici parce que c'est ici qu'on la cherchera.
 */
export function PageContact() {
  const consentement = useConsentementAuBlocTiers();
  // **Le cadre ne monte pas si son adresse est de la même origine que cette page** : son bac
  // à sable serait alors un no-op silencieux. Voir `leCadreVientDUneAutreOrigine`.
  const cadreMontable =
    leFormulaireEstOuvert() &&
    leCadreVientDUneAutreOrigine(ADRESSE_DU_FORMULAIRE, window.location.origin);

  return (
    <>
      <Section largeur="etroite">
        <Titre niveau={1}>Nous écrire</Titre>
        <Paragraphe>
          Swype se vend aux structures de soutien scolaire et aux tuteurs
          indépendants. Dites-nous qui vous êtes et ce que vous cherchez, on
          vous répond.
        </Paragraphe>
        <Paragraphe>
          N’écrivez rien sur un élève : ni nom, ni âge, ni classe. Cette page
          n’en a pas besoin, et nous n’avons pas à le savoir.
        </Paragraphe>
      </Section>

      <Section largeur="etroite" titre="Le formulaire">
        {!cadreMontable ? (
          /* **Prérequis 21, ouvert** : « obtenir le code embarqué du formulaire de contact
             auprès de la plateforme qui le génère », et il bloque « la page 5 seule ». On
             ne monte donc aucun cadre vide — voir `features/contact/contact.ts`. */
          /* **UN SEUL MESSAGE POUR DEUX CAUSES, ET C'EST DÉLIBÉRÉ.** Le formulaire n'est
             pas posé — prérequis 21 —, ou son adresse est de la même origine que cette page,
             ce qui annulerait son bac à sable. **Dans les deux cas la personne fait la même
             chose** : elle écrit par email. Distinguer les deux ne lui apprendrait rien et
             dirait à un visiteur comment le site est configuré. **La cause est visible en
             revue**, dans `leCadreVientDUneAutreOrigine`, et c'est là qu'elle sert. */
          <p className="rounded-3 bg-bandeau-attention-fond px-4 py-3 text-14 font-weight-500 leading-normal text-bandeau-attention-texte">
            Le formulaire n’est pas encore ouvert. En attendant, écrivez-nous
            par email : c’est le chemin ci-dessous, et il arrive au même
            endroit.
          </p>
        ) : !consentement.accorde ? (
          /* ═══════════════════════════════════════════════════════════════
             **LE BANDEAU DE CONSENTEMENT — S-24, ET LE CADRE N'EST PAS ENCORE MONTÉ.**
             ═══════════════════════════════════════════════════════════════

             Zone floue 8 du corpus, la seule de `CDC-6`, et **son repli est écrit** : ce qui
             reste ouvert est « ce que le bloc tiers dépose sur l'appareil de la personne — un
             identifiant, un compteur, rien — **et à quel moment il se charge** », son ancrage
             étant « aucun : **le code embarqué n'existe pas encore** ». Repli au lancement :
             « le bloc est traité **COMME S'IL DÉPOSAIT UN IDENTIFIANT** : la page qui le
             porte est **la seule** à en porter la conséquence, et tout consentement dû se
             limite à cette page. »

             **Ce que ce repli impose est un ORDRE et non un affichage** : le cadre n'est pas
             dans l'arbre avant le clic. Un bandeau posé au-dessus d'un cadre déjà chargé
             demande la permission **après** l'avoir prise, et ce défaut-là ne se voit pas à
             l'écran. */
          <div className="flex flex-col gap-3 rounded-4 border border-bord bg-fond-bloc p-4">
            <p className="text-14 leading-normal text-texte">
              Ce formulaire est fourni par une autre plateforme que Swype. En
              l’affichant, elle peut déposer un identifiant sur votre appareil.
              Rien de ce que vous y écrirez n’entre dans Swype.
            </p>
            <p className="text-13 text-texte-faible">
              Vous pouvez aussi nous écrire par email, sans rien afficher de
              cette plateforme.
            </p>
            <div className="flex flex-wrap gap-3">
              <Bouton onClick={consentement.accorder}>
                Afficher le formulaire
              </Bouton>
            </div>
          </div>
        ) : (
          /* **Un CADRE, jamais un script**, et le motif est dans
             `features/contact/contact.ts` : un script tiers s'exécuterait dans l'origine de
             ce site et lirait le document. `site/Dockerfile` tient la même borne une seconde
             fois — `script-src` reste sur `'self'`, seul `frame-src` porte la plateforme.

             **`title` est obligatoire sur un cadre** : sans lui, un lecteur d'écran annonce
             « cadre » et rien d'autre, ce qui rend le formulaire introuvable. */
          <iframe
            src={ADRESSE_DU_FORMULAIRE}
            title="Formulaire de contact"
            // **Aucun `allow`, et le bac à sable est explicite.** Le formulaire a besoin de
            // s'exécuter et de poster chez lui ; il n'a besoin ni de la caméra, ni du
            // micro, ni de sortir du cadre. `allow-same-origin` lui est nécessaire pour
            // parler à sa propre plateforme, et il ne lui donne rien de CE site — l'origine
            // dont il parle est la sienne.
            sandbox="allow-scripts allow-forms allow-same-origin"
            className="h-bloc-tiers w-full rounded-4 border border-bord bg-fond-surface"
          />
        )}
      </Section>

      <Section largeur="etroite" titre="Par email">
        {lAdresseEstOuverte() ? (
          <>
            <Paragraphe>
              Écrivez-nous depuis votre propre boîte. Rien ne passe par Swype.
            </Paragraphe>
            <a
              href={`mailto:${ADRESSE_EMAIL}`}
              className="text-18 font-weight-700 text-action underline underline-offset-2"
            >
              {ADRESSE_EMAIL}
            </a>
          </>
        ) : (
          /* **Prérequis 22, ouvert** : « aucune valeur d'adresse ici : le nom de la boîte se
             pose au versement ». Afficher une adresse qui ne reçoit rien serait pire que de
             n'en afficher aucune — « quelqu'un écrit à l'adresse affichée et personne ne lit
             la boîte → le produit n'en sait rien », et la personne non plus. */
          <Paragraphe>
            L’adresse de contact du groupe n’est pas encore ouverte.
          </Paragraphe>
        )}
      </Section>

      <Section largeur="etroite" titre="Ce que devient ce que vous écrivez">
        <Paragraphe>
          Rien de ce que vous envoyez n’entre dans Swype : ni en base, ni en
          fichier. Le formulaire appartient à une autre plateforme du groupe, et
          un email arrive dans une boîte. Swype n’apprend même pas qu’une
          demande est arrivée.
        </Paragraphe>
      </Section>
    </>
  );
}
